import { ORDERING_APP_IFRAME_URL } from "@/constants/orders";
import { useUser } from "@/hooks/use-user";
import { apiFetch } from "@/utils";
import { getSignedTransactionData } from "@/utils/transactions";
import { UtensilsCrossed, Sparkles, Loader2, ChevronLeft } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { usePosCartGuard } from "@/providers/PosCartGuardProvider";

const iframeTrustedOrigin = new URL(ORDERING_APP_IFRAME_URL).origin;

// TODO: Set to true when ready to launch the ordering iframe
const IFRAME_ENABLED = true;

export default function MenuPage() {
	const [, navigate] = useLocation()
	const iframeRef = useRef<HTMLIFrameElement>(null);
	const [isIframeLoaded, setIsIframeLoaded] = useState(false);

	const { setPosHasItems } = usePosCartGuard();

	const {
		uid,
		walletAddress,
		isConnected,
		balance,
		email,
		displayName,
		updateBalance
	} = useUser()

	const sendMessageToIframe = (type: string, payload: any) => {
		if(iframeRef.current) {
			console.log('Sending message to iframe:', {
				fromSechApp: true,
				type,
				payload
			});

			iframeRef.current.contentWindow?.postMessage({
				fromSechApp: true,
				type,
				payload
			}, '*');
		}
	}

	const sendUserDataToIframe = () => {
		sendMessageToIframe('user_data', {
			uid,
			walletAddress,
			balance,
			email,
			displayName
		})
	}

	useEffect(() => {
		if(!IFRAME_ENABLED) return;
		sendUserDataToIframe();
	}, [balance]);

	const onIframeLoad = () => {
		setIsIframeLoaded(true);
		setTimeout(() => {
			console.log('Sending user data to iframe');
			sendUserDataToIframe();
		}, 2000);

		// window.addEventListener('message', onIframeMessage);
	}

	useEffect(() => {
		if (!IFRAME_ENABLED) return;
	  
		window.addEventListener("message", onIframeMessage);
	  
		return () => {
		  window.removeEventListener("message", onIframeMessage);
		};
	}, []);

	const executePayment = async (paymentData: any) => {
		try {
			const { orderId, total, subtotal, tax_amount, taxBreakdown, items, location } = paymentData;
			const amount = typeof total.sech === 'number' ? total.sech.toFixed(5) : total.sech;

			let signature
			let nonce
			let forwardRequest
			// When user connects his wallet we should request signature to be able to finish transaction
			// in case with custodial wallet backend will handle it
			if(isConnected) {
				const {
					signedTx,
					txNonce,
					transferRequest: signatureData
				} = await getSignedTransactionData({
					userAddress: walletAddress,
					amount
				})
				signature = signedTx
				nonce = txNonce
				forwardRequest = signatureData
			}

			// Step 3: Send order + signed transaction to backend with new object structure
			const response = await apiFetch('/v1/orders/execute-order-payment', {
				method: 'POST',
				body: JSON.stringify({
					walletAddress,
					nonce,
					forwardRequest,
					signature,
					// Include new structured payment data
					orderId,
					total,
					subtotal,
					tax_amount,
					taxBreakdown,
					items,
					location
				}),
				withCredentials: true
			})

			const json = await response.json();

			const isSuccess = response.status === 200 && !!json.txHash

			sendMessageToIframe('payment_result', {
				success: isSuccess,
				orderId: orderId, // Include orderId in response
				...json
			});

			if(isSuccess) {
				await updateBalance();
			}
		} catch(err: any) {
			sendMessageToIframe('payment_result', {
				success: false,
				error: err.message
			});
		}
	}

	const onIframeMessage = (ev: MessageEvent<any>) => {
		if(ev.origin != iframeTrustedOrigin) {
			console.warn('Received iframe message from untrusted origin:', ev.origin);
			return;
		}

		const data = ev.data;

		if(!data.toSechApp) {
			return;
		}

		if(data.type === 'POS_CART_STATUS') {
			setPosHasItems(data.payload.hasItems)
			return
		}

		if(data.type === 'payment_request') {
			console.log('Payment Request:', data.payload)
			executePayment(data.payload);
		}
	}

	return (
		<div className="min-h-auto bg-[#18181B] h-full">
			<div className="max-w-5xl mx-auto px-5 pb-8 flex flex-col h-full">
				{/* Header with back button */}
				<div
					role="banner"
					className="sticky top-0 z-50 bg-[#18181B] flex items-center justify-start gap-2 text-white py-3 mb-[12px]"
				>
					<button
						type="button"
						aria-label="Go back"
						onClick={() => navigate('/')}
						className="h-8 w-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
					>
						<ChevronLeft className="h-[22px] w-[22px] text-[#ffffff]" />
					</button>
					<h1 className="text-[20px] leading-[18px] font-bold font-montserrat tracking-wide">
						Menu
					</h1>
				</div>

				{IFRAME_ENABLED && !isIframeLoaded && (
					<div className="absolute inset-0 bg-[var(--primary-background-color)] min-h-screen flex items-center justify-center">
						<Loader2 className="h-6 w-6 animate-spin text-gray-400"/>
					</div>
				)}

				{IFRAME_ENABLED ? (
					<iframe
						id="pos-app-iframe"
						onLoad={onIframeLoad}
						ref={iframeRef}
						src={ORDERING_APP_IFRAME_URL}
						className="w-full flex-1 rounded-xl"
						style={{ minHeight: "calc(100vh - 120px)" }}
					/>
				) : (
					<div className="flex-1 flex items-center justify-center w-full">
						<div className="text-center p-8">
							<div className="relative inline-block mb-6">
								<div className="absolute inset-0 animate-ping opacity-20">
									<UtensilsCrossed className="h-24 w-24 text-red-500"/>
								</div>
								<UtensilsCrossed className="h-24 w-24 text-red-500 relative"/>
								<Sparkles className="h-8 w-8 text-yellow-500 absolute -top-2 -right-2 animate-pulse"/>
							</div>
							<h1 className="text-4xl font-bold text-[#ffffff] mb-3">
								Something Delicious is Cooking!
							</h1>
							<p className="text-xl text-[#BABABB] mb-2">
								Hotel Services Coming Soon
							</p>
							<p className="text-sm text-[#888888]">
								Order food, drinks & more with your SECH tokens
							</p>
							<div className="mt-8 flex justify-center gap-2">
								{[0, 1, 2].map((i) => (
									<div
										key={i}
										className="w-3 h-3 bg-red-500 rounded-full animate-bounce"
										style={{ animationDelay: `${i * 0.15}s` }}
									/>
								))}
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	)
}